import { EurekaPackageType, type IPackageType } from '@eureka/services/src/apis/common.api.types';
import { isUserLogin, accountDeferred } from '@eureka/utils/account';
import { packageInfoDeferred } from '@eureka/utils/package-info';
import { createSharedComposable, tryOnBeforeMount } from '@vueuse/core';
import { computed, ref } from 'vue';
import type { ISubscriptionListDTO } from '../types';
import { PAID_STATUS, ESubscriptionStatus } from '../types';
import _ from 'lodash';
import { cancelSubscription, getInvoices, getSubscriptionList, resumeSubscription, createPaymentSession } from '../apis/stripe-api';
import type { Account } from '@patsnap-utils/biz';
import { controlDeferred } from '@eureka/utils/control';
import type { IControl } from '@eureka/utils/control';

export const usePlg = createSharedComposable(() => {
    const packageInfo = ref<IPackageType[]>([])
    const productType = ref<string>('')
    const control = ref<IControl>()

    async function fetchPackageInfo() {
        const isLogin = await isUserLogin();
        const res = isLogin ? await packageInfoDeferred.promise : [];
        packageInfo.value = res;
    }

    // 检测套餐付款状态
    function getPaidStatus(eureka_packages: string[], beforeCheck?: (pkgs: EurekaPackageType[]) => boolean) {
        // 获取已购商业套餐
        const commercialPkgs = _.filter(packageInfo.value, (pkg) => pkg.package_type === 'COMMERCIAL').map(pkg => pkg.eureka_package) as EurekaPackageType[];

        // 前置检查，如果已购套餐中存在比当前高的版本，则跳出检查，返回低版本标识
        if (beforeCheck?.(commercialPkgs)) {
            return PAID_STATUS.LOW_VERSION;
        }
        // 获取交集套餐
        const intersectionPkgs = _.intersection(eureka_packages, commercialPkgs);
        return intersectionPkgs.length > 0 ? PAID_STATUS.PAID : PAID_STATUS.UNPAID;
    }

    function setProductType(type: string) {
        productType.value = type;
    }

    const currentSubscription = ref<ISubscriptionListDTO | null>(null);

    const account = ref<Account | null>(null);

    // 数据是否已加载完成
    const isDataReady = ref(false);

    // 企业用户
    const isEnterpriseTenant = computed(() => {
        return !!account.value?._info?.company_id;
    })

    const isPersonal = computed(() => {
        return !isEnterpriseTenant.value;
    })

    const isPro = computed(() => {
        // 对首页来说有一个订阅Id符合就是pro
        return isPersonal.value && !!currentSubscription.value;
    })

    const isShowUpgrade = computed(() => {
        // 数据未加载完成时返回 false，避免闪烁
        if (!isDataReady.value) return false;
        return isPersonal.value && !currentSubscription.value;
    })

    /**
     * 获取用户订阅列表
     */
    async function fetchSubscriptionList() {
        const isLogin = await isUserLogin();
        const res = isLogin ? await getSubscriptionList() : [];
        const pkgs = res.filter(item => [ESubscriptionStatus.active, ESubscriptionStatus.trialing].includes(item.subscription_status));

        const priceIDList = [
            import.meta.env.VITE_STRIPE_PRICE_DRAFTING_MONTHLY,
            import.meta.env.VITE_STRIPE_PRICE_DRAFTING_YEARLY,
            import.meta.env.VITE_STRIPE_PRICE_SEARCHING_MONTHLY,
            import.meta.env.VITE_STRIPE_PRICE_SEARCHING_YEARLY
        ]

        // 过滤出 items 中 price_id 在 priceIDList 中的订阅
        const filteredPkgs = pkgs.filter(pkg =>
            pkg.items?.some(item => priceIDList.includes(item.price_id))
        );

        if (filteredPkgs.length > 0 && filteredPkgs[0]) {
            currentSubscription.value = filteredPkgs[0];
        }
    }

    /**
     *
     * @returns 查看账单
     */
    async function handleViewInvoices() {
        if (!currentSubscription.value) return;
        const res = await getInvoices({
            subscription_id: currentSubscription.value.subscription_id,
            return_url: window.location.href,
        });
        if (!res) return;
        window.open(res, '_blank', 'noopener,noreferrer');
    }

    /**
     * 取消订阅
     * @returns
     */
    async function handleCancelPlan(reason: string, otherReason?: string) {
        if (!currentSubscription.value) return;
        await cancelSubscription({
            subscription_id: currentSubscription.value.subscription_id,
            reason,
            ...(otherReason ? { remark: otherReason } : {}),
        });
    }

    /**
     * 恢复订阅
     * @returns
     */
    async function handleResumePlan() {
        if (!currentSubscription.value) return;
        await resumeSubscription({
            subscription_id: currentSubscription.value.subscription_id,
        });
    }

    const isEnterprise = computed(() => {
        return getPaidStatus([EurekaPackageType.AIR_COMMERCIAL]) === PAID_STATUS.PAID;
    })

    const isBasic = computed(() => {
        return !isPro.value && !isEnterprise.value;
    })

    // TODO: 临时
    const userEdition = computed(() => {
        return isEnterprise.value ? 'enterprise' : isPro.value ? 'rd_pro' : 'basic';
    })

    /**
     * 转换成前端的i18n类型
     */
    const planType = computed(() => {
        const map = {
            free_trial: 'freeTrial',
            basic: 'basic',
            rd_pro: 'pro',
            enterprise: 'enterprise',
        } as const;
        return map[userEdition.value as keyof typeof map];
    })

    /**
     * 是否取消了订阅
     */
    const isCanceled = computed(() => {
        return !!currentSubscription.value?.cancel_at;
    })

    const isFreemium = computed(() => {
        return control.value?.freemium;
    })

    const isCreditsAvailable = computed(() => {
        return control.value?.credit_package;
    })

    const isCountPackageAvailable = computed(() => {
        return control.value?.count_package;
    })

    tryOnBeforeMount(async() => {
        control.value = await controlDeferred.promise;
        account.value = await accountDeferred.promise;
    })

    return {
        fetchPackageInfo,
        getPaidStatus,
        setProductType,
        packageInfo,
        productType,
        fetchSubscriptionList,
        handleViewInvoices,
        handleCancelPlan,
        handleResumePlan,
        isPro,
        isBasic,
        planType,
        isCanceled,
        userEdition,
        currentSubscription,
        createPaymentSession,
        isEnterprise,
        isPersonal,
        isFreemium,
        isCreditsAvailable,
        isCountPackageAvailable,
        isEnterpriseTenant,
        isShowUpgrade,
    }
})
